import React, { Fragment } from "react";
import { lazyWithRetry } from "../../core/lazy_with_retry";
import { useI18n } from "../../lib/i18n/i18n_provider";

import { OfferingDetailsDayPass } from "../template_offering_details_daypass";
import { useMDotAnalytics } from "./mdot_analytics";
import {
  dayPassGeneralInfoEN,
  dayPassGeneralInfoHE,
  partnerConfig,
  testimonialsEN,
  testimonialsHE,
} from "./mdot_partner_config";

const Theme = lazyWithRetry(() => import("./mdot_theme"));

const faqsEN = [
  {
    question: "What are your operating hours?",
    answer:
      "We are open for daily on-demand booking on Sunday-Thursday, 8:30 am to 5:30 pm. We would be happy to have you as our guest during these operating hours.",
  },
  {
    question: "Can I remain after 5:30 pm?",
    answer:
      "Access to daily guests is limited to operating hours only. It is not permitted to remain in the complex beyond these hours.",
  },
  {
    question: "How can I change or cancel a booking?",
    answer:
      "Bookings can be changed or canceled up to 24 hours before the time of the booking (i.e., by 8:30 am on the day before your reservation), at the latest.",
  },
  {
    question: "What services are included in the Day Pass?",
    answer: `A Day Pass will provide you with a pleasant and convenient work area, ergonomic workstations in an open space (subject to availability),
        access to the common spaces, fast and secure Internet and full access to the accessorized kitchen.
      `,
  },
  {
    question:
      "What happens if I book a Day Pass and no workstations are available in the open space?",
    answer: `Booking a Day Pass gives you access for the day to the complex rather than to an office or workstation.
      Although possible, it is highly unlikely that no workstations will be available on the day of your arrival. 
      We make every effort to limit the number of daily bookings so that enough workstations will be available for those who need them.
      In any case, many of our guests prefer to work in our lovely garden or in one of the common seating spaces in the complex.
    `,
  },
  {
    question: "Where can I park?",
    answer: `What could be better than parking in a reserved spot in the building? For that option,
              contact us by email at info@m-dot.co.il and book the number of parking spaces you need in advance.
              Parking spaces are subject to availability and an additional fee.
              Alternately, free parking is available within a short walking distance,
              in an unpaved parking lot next to the Hermozo banquet hall (entry via HaZriya Street).
            `,
  },
  {
    question: "Where can I eat?",
    answer: `Food can be ordered in from a wide variety of restaurants in Modiin, or you can avail yourself of the dining
              room in the building which offers a delicious kosher lunch for an additional fee. You can always grab a snack from the snack dispenser
              in the lobby, or bring food from home, which you can store in the refrigerator and heat up in the microwave in the main kitchen.
            `,
  },
  {
    question: "Is it possible to make phone calls in the open space?",
    answer:
      "The open space is designated as a “quiet zone” where phone calls are not allowed. If you need to, you can use one of the telephone booths in the complex.",
  },
  {
    question: "Can I invite guests?",
    answer:
      "Daily entry does not include guests. If you wish to invite guests, you can book one of our meeting rooms for a separate fee, subject to availability.",
  },
  {
    question: "Is there a printer available for use?",
    answer:
      "A printer is available for your use in the administration area. It can be used for a separate fee.",
  },
];

const faqsHE = [
  {
    question: "מהן שעות הפעילות שלכם?",
    answer:
      "המתחם פתוח להזמנות יומיות בימים א'-ה' בין השעות 08:30 - 17:30. נשמח לארח אותך אצלנו במסגרת שעות פעילות אלה.",
  },
  {
    question: "האם אפשר להישאר אחרי 17:30?",
    answer:
      "הגישה לאורחים יומיים מוגבלת לשעות הפעילות בלבד. לא ניתן להישאר במתחם מעבר לשעות אלה",
  },
  {
    question: "איך אפשר לשנות או לבטל הזמנה?",
    answer:
      "ניתן לשנות או לבטל הזמנה עד 24 שעות לפני המועד שהוזמן (לכל המאוחר עד לשעה 08:30 בבוקר ביום שלפני מועד ההזמנה).",
  },
  {
    question: "איזה שירותים כלולים  ב DAY PASS ?",
    answer: `בהזמנת כניסה יומית תוכלו להינות מסביבת עבודה נוחה ונעימה, מעמדת עבודה ארגונומית בחלל האופן ספייס
     (ע"ב מקום פנוי), גישה לחללים הציבוריים, אינטרנט מהיר ומאובטח ושימוש חופשי במטבח
      `,
  },
  {
    question:
      "האם יכול להיות מצב שבו אזמין כניסה יומית ולא תהיה לי עמדה פנויה באופן ספייס?",
    answer: `הזמנת DAY PASS מעניקה לך גישה יומית למתחם ולא למשרד או לעמדת עבודה. יכול להיות, אם כי לא מאד סביר,
     שעם הגתך עלא תהיה עמדה פנויה. אנחנו מקפידות ככל יכולתנו להגביל את ההזמנות היומיות על מנת
     שכל אחד שירצה בכך יוכל לתפוס עמדה, ובכל מקרה, רבים מהאורחים שלנו מראש
     מעדיפים לעבוד בגינה היפה או באחת מפינות הישיבה הבלתי פורמאליות במתחם.`,
  },
  {
    question: "איפה חונים?",
    answer: `ניתן לחנות ברחוב או במגרש חניה חינמי הנמצא במרחק 5 דקות הליכה (סמוך לאולם האירועים "הרמוזו",
     כניסה מרחוב "הזריעה"). בהזמנה מראש, ע"ב מקום פנוי ובתוספת תשלום, ניתן לשריין חניה שמורה בבניין`,
  },
  {
    question: "איפה אוכלים?",
    answer: `ניתן להזמין משלוחים ממגון מסעדות במודיעין או לעלות לחדר האוכל
     שבבניין המציע בופה צהריים עשיר וכשר (בתשלום נפרד). בנוסף תוכלו לסגור את הפינה עם
     חטיפים מהמכונה שבלובי או להביא איתכם אוכל מהבית, לאחסן במקרר ולחמם במיקרו שבמטבח הראשי .`,
  },
  {
    question: "האם אפשר לקיים שיחות טלפון באופן ספייס?",
    answer: `אזור האופן ספייס מוגדר כ"אזור עבודה שקט" שבו לא ניתן לקיים שיחות טלפון. במקרה הצורך תוכלו לעשות שימוש באחד מתאי הטלפון העומדים לרשותכם במתחם`,
  },
  {
    question: "האם ניתן להזמין אורחים?",
    answer:
      "כניסה יומית אינה כוללת אורחים. במידה ותרצו להזמין אורחים תוכלו לשריין את אחד מחדרי הישיבות שלנו, על בסיס מקום פנוי ובתוספת תשלום.",
  },
  {
    question: "האם יש מדפסת זמינה?",
    answer:
      "בפינת האדמיניסטרציה במתחם עומדת לרשותכם מכונת צילום / מדפסת. ניתן לעשות בה שימוש בתשלום נפרד",
  },
];

export function OfferingMDotDayPass() {
  useMDotAnalytics();

  const { locale } = useI18n();

  return (
    <Fragment>
      <Theme />
      <OfferingDetailsDayPass
        showAddress={false}
        showDirections
        partnerConfig={partnerConfig}
        data={{
          testimonials:
            locale.baseLanguageTag === "en" ? testimonialsEN : testimonialsHE,
          faqs: locale.baseLanguageTag === "en" ? faqsEN : faqsHE,
          generalInfo:
            locale.baseLanguageTag === "en"
              ? dayPassGeneralInfoEN
              : dayPassGeneralInfoHE,
        }}
      />
    </Fragment>
  );
}
